import React, { useMemo, useState } from 'react';
import { Box, Button, Center, Flex, Grid, Loader, Paper, Stack, Text, Title } from '@mantine/core';
import { IconListDetails, IconPlus } from '@tabler/icons-react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { PageContainer } from '../../../components/ui/PageContainer';
import { useGetPerformanceReviewRounds } from '../../../api/generated/performance-review/performance-review';
import { PerformanceReviewRoundCreateModal } from '../../../components/review/PerformanceReviewRoundCreateModal';
import { PerformanceReviewRound } from '../../../api/generated/model/performanceReviewRound';
import { useDevice } from '../../../hooks/useDevice';
import { useHasFeature } from '../../../store/featureFlagsStore';
import { Eyebrow } from '../parts/primitives';
import { AdminStats } from './parts/AdminStats';
import { FilterBar, FilterValue } from './parts/FilterBar';
import { RoundCard } from './parts/RoundCard';
import { TipsCard } from './parts/TipsCard';
import { RecentActivity } from './parts/RecentActivity';

export const PerformanceReviewAdmin: React.FC = () => {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const device = useDevice();
  const isMobile = device === 'mobile';

  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const isBuilderOn = useHasFeature('questionnaireBuilder');
  const [filter, setFilter] = useState<FilterValue>('all');
  const [search, setSearch] = useState('');

  const { data: rounds = [], isLoading } = useGetPerformanceReviewRounds();
  const locale = i18n.language?.startsWith('en') ? 'en-US' : 'ru-RU';

  const list = useMemo(() => (rounds as unknown as PerformanceReviewRound[]) || [], [rounds]);

  const counts = useMemo<Record<FilterValue, number>>(
    () => ({
      all: list.length,
      active: list.filter((r) => r.status === 'active').length,
      draft: list.filter((r) => r.status === 'draft').length,
      closed: list.filter((r) => r.status === 'closed').length,
      archived: list.filter((r) => r.status === 'archived').length,
    }),
    [list],
  );

  const filtered = useMemo(() => {
    const byStatus = filter === 'all' ? list : list.filter((r) => r.status === filter);
    const q = search.trim().toLowerCase();
    if (!q) return byStatus;
    return byStatus.filter(
      (r) => r.title?.toLowerCase().includes(q) || r.description?.toLowerCase().includes(q),
    );
  }, [list, filter, search]);

  const stats = useMemo(
    () => ({
      activeRounds: counts.active,
      drafts: counts.draft,
      closed: counts.closed + counts.archived,
      total: counts.all,
    }),
    [counts],
  );

  if (isLoading) {
    return (
      <PageContainer>
        <Center mih="60vh">
          <Loader size="sm" />
        </Center>
      </PageContainer>
    );
  }

  const handleOpen = (id: string) => navigate(`/performance-review/admin/${id}`);

  return (
    <PageContainer>
      <Stack gap="xl">
        {/* Header */}
        <Flex justify="space-between" align="flex-end" gap="md" wrap="wrap">
          <Box>
            <Eyebrow>{t('perfReview.adminHome.eyebrow')}</Eyebrow>
            <Title order={2} mt={6}>
              {t('perfReview.adminHome.title')}
            </Title>
          </Box>
          <Flex gap="xs" wrap="wrap">
            {isBuilderOn && (
              <Button
                variant="subtle"
                leftSection={<IconListDetails size={14} />}
                onClick={() => navigate('/performance-review/admin/questionnaires')}
              >
                {t('perfReview.questionnaires.title')}
              </Button>
            )}
            <Button
              variant="light"
              leftSection={<IconPlus size={14} />}
              onClick={() => setIsCreateOpen(true)}
            >
              {t('perfReview.createRound')}
            </Button>
          </Flex>
        </Flex>

        <AdminStats stats={stats} t={t} />

        <Grid gutter="lg">
          <Grid.Col span={{ base: 12, lg: 8 }}>
            <Stack gap="md">
              <FilterBar
                value={filter}
                onChange={setFilter}
                counts={counts}
                search={search}
                onSearch={setSearch}
                isMobile={isMobile}
              />
              {filtered.length === 0 ? (
                <Paper withBorder p="xl" radius="lg">
                  <Text c="dimmed" ta="center" size="sm">
                    {t('perfReview.adminHome.emptyFilter')}
                  </Text>
                </Paper>
              ) : (
                <Stack gap="sm">
                  {filtered.map((r) => (
                    <RoundCard
                      key={r._id}
                      round={r}
                      onOpen={handleOpen}
                      locale={locale}
                      isMobile={isMobile}
                    />
                  ))}
                </Stack>
              )}
            </Stack>
          </Grid.Col>
          <Grid.Col span={{ base: 12, lg: 4 }}>
            <Stack
              gap="md"
              style={device === 'desktop' ? { position: 'sticky', top: 80 } : undefined}
            >
              <TipsCard onCreate={() => setIsCreateOpen(true)} />
              <RecentActivity rounds={list} locale={locale} />
            </Stack>
          </Grid.Col>
        </Grid>
      </Stack>

      <PerformanceReviewRoundCreateModal
        opened={isCreateOpen}
        onClose={() => setIsCreateOpen(false)}
        onCreated={(id) => navigate(`/performance-review/admin/${id}`)}
      />
    </PageContainer>
  );
};
